import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import type {ExpenseReportGeneratorQuery} from '@/graphql-types'
import useCurrency from '@/hooks/use-currency'
import RichText from '@helios/shared/components/rich-text'
import logoHorizontal from '@helios/shared/images/icons/logo-horizontal.svg'
import menuCloseIcon from '@helios/shared/images/icons/menu-close.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

import {BREAKPOINT_SM} from '../constants'
import {useCurrencyFormat} from '../hooks'
import type {Expense, ExpenseReportGeneratorVariant} from '../types'

const isValidExpense = (expense: Expense) =>
    Object.values({
        date: expense.date,
        merchant: expense.merchant,
        category: expense.category,
        project: expense.project,
        amount: expense.amount,
    }).some((val) => !!val)

interface Props {
    expenses: Expense[]
    fullName?: string
    companyName?: string
    reportNumber?: string
    companyLogoUrl: string | null
    onClosePreview: () => void
    content: PrismicData<ExpenseReportGeneratorQuery, 'allPrismicExpenseReportGenerator'>['data']
    variant: ExpenseReportGeneratorVariant
}

const Preview = ({
    expenses,
    fullName,
    companyName,
    reportNumber,
    companyLogoUrl,
    onClosePreview,
    content,
    variant,
}: Props) => {
    const currency = useCurrency()
    const currencyFormat = useCurrencyFormat()
    const globalStrings = useGlobalStrings()

    const amountTotal = expenses.reduce((sum, e) => sum + (e.amount ?? 0), 0)
    const formattedTotal = currencyFormat.format(amountTotal)

    React.useEffect(() => {
        track(TrackingEvent.ERGActioned, {
            action: 'preview_opened',
            variant,
        })
    }, [variant])

    return (
        <>
            <PageStyles />

            <OuterSpacing padding="small">
                <Wrapper>
                    <CloseBtn
                        onClick={onClosePreview}
                        aria-label={globalStrings.close_button_text}
                    />

                    <HeaderCTAWrapper>
                        <Logo src={logoHorizontal} alt={globalStrings.home} />

                        <RichText
                            variant="spezia"
                            color="muted"
                            content={content.madeByPleo.richText}
                        />
                    </HeaderCTAWrapper>

                    <Disclaimer variant="paragraph-base" color="muted" center>
                        {content.previewDisclaimer}
                    </Disclaimer>

                    <Details>
                        <CompanyDetails>
                            <CompanyLogo
                                placeholderLogoURL={content.almostThere[0].logoPlaceholder.url}
                                logoURL={companyLogoUrl}
                            />

                            <Text as="h2" variant="title-base">
                                {companyName || globalStrings.company_name}
                            </Text>
                        </CompanyDetails>

                        <ExpenseDetails>
                            <Text as="h2">{content.expenseReport}</Text>

                            <Text variant="paragraph-base" color="muted">
                                {content.reportNumber} {reportNumber}
                                <br />
                                {content.employee} {fullName}
                            </Text>
                        </ExpenseDetails>
                    </Details>

                    {expenses.filter(isValidExpense).map((expense, i) => (
                        <ExpenseCard key={expense.id}>
                            <NameValuePair>
                                <Text as="h3" variant="paragraph-base" color="muted">
                                    {content.expense} {i + 1}
                                </Text>
                                <Text>{expense.description}</Text>
                            </NameValuePair>

                            <ExpenseCardGrid>
                                {expense.date && (
                                    <ExpenseField name={content.date} value={expense.date} />
                                )}

                                {expense.merchant && (
                                    <ExpenseField
                                        name={content.merchant}
                                        value={expense.merchant}
                                    />
                                )}

                                {expense.project && (
                                    <ExpenseField name={content.project} value={expense.project} />
                                )}

                                {expense.category && (
                                    <ExpenseField
                                        name={content.category}
                                        value={expense.category}
                                    />
                                )}

                                {expense.amount && (
                                    <ExpenseField
                                        name={`${content.amount} (${currency.symbol})`}
                                        value={expense.amount}
                                    />
                                )}
                            </ExpenseCardGrid>
                        </ExpenseCard>
                    ))}

                    <Footer>
                        <FooterTotal>
                            <Text variant="title-base">{content.total}</Text>
                            <Text variant="title-base">{formattedTotal}</Text>
                        </FooterTotal>

                        <SubmittedBy>
                            <Text variant="paragraph-base" color="muted">
                                {content.submittedBy} {fullName}
                            </Text>
                        </SubmittedBy>
                    </Footer>

                    <FooterCTAWrapper>
                        <RichText
                            variant="paragraph-base"
                            color="muted"
                            content={content.previewFooter.richText}
                        />
                    </FooterCTAWrapper>
                </Wrapper>
            </OuterSpacing>
        </>
    )
}

interface ExpenseFieldProps {
    name: string
    value: string | number
}

const ExpenseField = ({name, value}: ExpenseFieldProps) => (
    <NameValuePair>
        <Text variant="label-base-medium" as="h4" color="brand">
            {name}
        </Text>

        <Text variant="paragraph-base">{value}</Text>
    </NameValuePair>
)

const OuterSpacing = styled(Section)`
    ${mq.between(BREAKPOINT_SM, 'lg')} {
        padding-top: 80px;
    }

    @media print {
        padding: 0;
    }
`

const Wrapper = styled.div`
    position: relative;
    display: grid;
    row-gap: ${tokens.spacing24};
    width: 100%;
    max-width: 880px;
    margin: 0 auto;
    background: ${tokens.shade000};

    ${mq.min(BREAKPOINT_SM)}, print {
        row-gap: ${tokens.spacing28};
        padding: ${tokens.spacing60};
        box-shadow: ${tokens.shadowRaiseMedium};
    }

    /* The break-inside property doesn't work with grid layouts, so we make it block and use margin instead of gap */
    @media print {
        display: block;
        max-width: unset;
        padding: 0;
        box-shadow: unset;

        > * + * {
            margin-top: ${tokens.spacing24};
        }
    }
`

const HeaderCTAWrapper = styled.div`
    display: none;
    justify-content: space-between;
    column-gap: ${tokens.spacing20};
    grid-auto-flow: column;
    align-items: center;

    ${mq.min(BREAKPOINT_SM)}, print {
        display: grid;
    }
`

const Logo = styled.img`
    width: 60px;
`

const Disclaimer = styled(Text)`
    ${mq.min(BREAKPOINT_SM)}, print {
        display: none;
    }
`

const ExpenseCardGrid = styled.div`
    display: grid;
    row-gap: ${tokens.spacing16};
    column-gap: ${tokens.spacing16};
    grid-template-columns: repeat(2, 1fr);

    ${mq.min(BREAKPOINT_SM)}, print {
        column-gap: ${tokens.spacing10};
        grid-template-columns: unset;
        grid-auto-flow: column;
    }
`

const ExpenseCard = styled.div`
    display: grid;
    row-gap: ${tokens.spacing16};
    padding: ${tokens.spacing16};
    background: ${colors.background.muted};
    border-radius: ${radii.arc8};

    ${mq.min(BREAKPOINT_SM)}, print {
        row-gap: ${tokens.spacing20};
        padding: ${tokens.spacing20};
    }

    @media print {
        break-inside: avoid;
        background: none;
        border: 1px solid ${colors.border.default};
    }
`

const NameValuePair = styled.div`
    display: grid;
    row-gap: ${tokens.spacing4};
`

const Details = styled.header`
    display: grid;
    row-gap: ${tokens.spacing20};
    column-gap: ${tokens.spacing20};
    padding: ${tokens.spacing16};
    text-align: center;

    ${mq.min(BREAKPOINT_SM)}, print {
        grid-template-columns: repeat(2, auto);
        padding: ${tokens.spacing24};
        justify-content: space-between;
        text-align: left;
    }
`

const CompanyDetails = styled.div`
    display: grid;
    justify-items: center;
    row-gap: ${tokens.spacing8};
    align-items: center;

    ${mq.min(BREAKPOINT_SM)}, print {
        justify-items: start;
        grid-auto-flow: column;
        column-gap: ${tokens.spacing12};
    }
`

const ExpenseDetails = styled.div`
    display: grid;
    justify-items: center;
    row-gap: ${tokens.spacing8};
    align-items: center;

    ${mq.min(BREAKPOINT_SM)}, print {
        justify-items: start;
    }
`

interface CompanyLogoProps {
    placeholderLogoURL: string
    logoURL: string | null
}

const CompanyLogo = styled.div<CompanyLogoProps>`
    width: 36px;
    height: 36px;
    background-color: ${({logoURL}) => (logoURL ? 'transparent' : colors.background.pink)};
    background-image: url(${({logoURL, placeholderLogoURL}) => logoURL || placeholderLogoURL});
    background-repeat: no-repeat;
    background-position: center;
    background-size: ${({logoURL}) => (logoURL ? '100%' : '80%')};
    border-radius: ${tokens.arc4};

    ${mq.min(BREAKPOINT_SM)}, print {
        width: 48px;
        height: 48px;
    }
`

const Footer = styled.footer`
    display: grid;
    row-gap: 80px;
    padding: ${tokens.spacing16};

    ${mq.min(BREAKPOINT_SM)}, print {
        padding: 0;
        margin-top: ${tokens.spacing12};
        row-gap: 100px;
    }
`

const FooterTotal = styled.div`
    display: grid;
    width: 100%;
    grid-auto-flow: column;
    column-gap: ${tokens.spacing40};
    justify-content: space-between;

    ${mq.min(BREAKPOINT_SM)}, print {
        justify-content: end;
    }
`

const SubmittedBy = styled.div`
    padding-top: ${tokens.spacing10};
    border-top: 1px solid ${colors.border.secondary};

    ${mq.min(BREAKPOINT_SM)}, print {
        max-width: 320px;
    }
`

const FooterCTAWrapper = styled.div`
    text-align: center;

    ${mq.min(BREAKPOINT_SM)}, print {
        margin-top: ${tokens.spacing28};
        text-align: left;
    }
`

const CloseBtn = styled.button`
    position: absolute;
    right: 0;
    bottom: calc(100% + ${tokens.spacing32});
    width: 24px;
    height: 24px;
    padding: unset;
    cursor: pointer;
    background: unset;
    background-image: url(${menuCloseIcon});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    border: unset;

    ${mq.min('lg')} {
        top: 0;
        right: unset;
        bottom: unset;
        left: calc(100% + ${tokens.spacing32});
    }

    ${mq.max(BREAKPOINT_SM)} {
        display: none;
    }

    @media print {
        display: none;
    }
`

const PageStyles = createGlobalStyle`
    html,
    body {
        background: ${tokens.shade000};

        ${mq.min(BREAKPOINT_SM)} {
            background: ${tokens.shade200};
        }

        @media print {
            background: ${tokens.shade000} !important;
        }
    }

    @page {
        size: a4 portrait;

        /* this affects the margin in the printer settings */
        margin: ${tokens.spacing60};
    }
`

export default Preview
